import React, { useState } from 'react';
import { POPUP_FORM_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  AdminPanelTitle,
  AdminDetailFieldLabel,
  AdminSupportingText,
  AdminAlertText,
  AdminRowAction,
  AdminLinkAction,
  AdminFormFieldError,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  adminFormInputClasses,
  adminFormTextareaClasses,
} from '../shared/adminUx';
import { REPORT_AREAS, buildOnDemandReportName } from './adminReportsData';

/**
 * On-demand PDF generation form — modal/sheet pattern aligned with admin notes modals,
 * content width per frontend-ui-consistency (narrow popup column).
 */
export default function GenerateReportModal({ open, onClose, onGenerate }) {
  const [reportArea, setReportArea] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const fieldClass = adminFormInputClasses(false);
  const textareaClass = adminFormTextareaClasses(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reportArea || !dateFrom || !dateTo) {
      setError('Module and date range are required.');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('End date must be on or after start date.');
      return;
    }
    setError('');
    onGenerate({
      reportArea,
      reportName: buildOnDemandReportName(reportArea, dateFrom, dateTo),
      dateFrom,
      dateTo,
      notes: notes.trim(),
    });
    setReportArea('');
    setDateFrom('');
    setDateTo('');
    setNotes('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`bg-white rounded-xl shadow-lg ${POPUP_FORM_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 pt-5 pb-2">
          <AdminPanelTitle className="text-offblack text-center flex-1">Generate Report</AdminPanelTitle>
          <button
            type="button"
            onClick={onClose}
            className="text-offblack hover:text-primaryblue shrink-0"
            aria-label="Close"
          >
            <i className="fi fi-rs-cross dashboard-icon" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
          <div>
            <AdminDetailFieldLabel>
              Module <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <select
              value={reportArea}
              onChange={(e) => setReportArea(e.target.value)}
              className={`${fieldClass} w-full`}
            >
              <option value="">Select module</option>
              {REPORT_AREAS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <AdminDetailFieldLabel>
                From <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={`${fieldClass} w-full`}
              />
            </div>
            <div>
              <AdminDetailFieldLabel>
                To <span className="text-alertred">*</span>
              </AdminDetailFieldLabel>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={`${fieldClass} w-full`}
              />
            </div>
          </div>
          <AdminSupportingText className="text-mediumgray">Select date range covered by the report.</AdminSupportingText>
          <div>
            <AdminDetailFieldLabel>Notes</AdminDetailFieldLabel>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add short report note"
              rows={3}
              className={`${textareaClass} w-full resize-y min-h-[80px]`}
            />
          </div>
          {error ? <AdminAlertText className="text-xs">{error}</AdminAlertText> : null}
          <div className="flex justify-end gap-3 pt-2">
            <AdminLinkAction type="button" onClick={onClose} className="px-3 py-2">
              Cancel
            </AdminLinkAction>
            <AdminHeaderAction type="submit">
              Generate
            </AdminHeaderAction>
          </div>
        </form>
      </div>
    </div>
  );
}
